import React, { useState, useEffect } from 'react'
import { useApi } from '../../contexts/ApiContext'
import { useUserId } from '../../hooks/useUserId'
import { supabase } from '../../lib/supabase'
import { trackPageView } from '../../utils/eventTracking'
import { 
  FileText, 
  Download, 
  Eye,
  Calendar,
  CheckCircle,
  Clock,
  XCircle,
  AlertCircle,
  Filter,
  Search,
  Upload,
  File,
  Image as ImageIcon,
  FileCheck,
  X,
  Trash2
} from 'lucide-react'
import toast from 'react-hot-toast'

const CustomerDocuments = () => {
  const { getDocuments, createTableRecord, deleteTableRecord, getUserPolicies, getUserPoliciesWithClaims } = useApi()
  const { userId } = useUserId()
  const [documents, setDocuments] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [typeFilter, setTypeFilter] = useState('all')
  const [statusFilter, setStatusFilter] = useState('all')
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedDocument, setSelectedDocument] = useState(null)
  const [showViewModal, setShowViewModal] = useState(false)
  const [showUploadModal, setShowUploadModal] = useState(false)
  const [uploadFile, setUploadFile] = useState(null)
  const [uploading, setUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [uploadDocType, setUploadDocType] = useState('CLAIM_DOCUMENT')
  const [uploadCategory, setUploadCategory] = useState('MEDICAL')
  const [uploadDescription, setUploadDescription] = useState('')
  const [uploadSubcategory, setUploadSubcategory] = useState('')
  const [uploadTags, setUploadTags] = useState('')
  const [uploadRelatedClaim, setUploadRelatedClaim] = useState('')
  const [uploadRelatedPolicy, setUploadRelatedPolicy] = useState('')
  const [userPolicies, setUserPolicies] = useState([])
  const [userClaims, setUserClaims] = useState([])

  // Track page view
  useEffect(() => {
    trackPageView('Customer Documents', { user_id: userId })
  }, [userId])

  useEffect(() => {
    if (userId) {
      loadDocuments()
    }
  }, [userId])

  const loadDocuments = async () => {
    try {
      setLoading(true)
      const documentsData = await getDocuments()
      
      // Filter documents for logged-in user by customer_id
      const userDocuments = documentsData?.filter(doc => doc.customer_id === userId) || []
      setDocuments(userDocuments)
    } catch (err) {
      console.error('Error loading documents:', err)
      setError('Failed to load documents')
    } finally {
      setLoading(false)
    }
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  const formatFileSize = (bytes) => {
    if (!bytes) return 'N/A'
    if (bytes < 1024) return bytes + ' B'
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
  }

  const getStatusColor = (status) => {
    switch (status?.toUpperCase()) {
      case 'VERIFIED':
      case 'APPROVED':
        return 'bg-green-100 text-green-800 border-green-200'
      case 'PENDING_VERIFICATION':
      case 'PENDING':
      case 'UNDER_REVIEW':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200'
      case 'REJECTED':
      case 'EXPIRED':
        return 'bg-red-100 text-red-800 border-red-200'
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200'
    }
  }

  const getStatusIcon = (status) => {
    switch (status?.toUpperCase()) {
      case 'VERIFIED':
      case 'APPROVED':
        return <CheckCircle className="h-4 w-4" />
      case 'PENDING_VERIFICATION':
      case 'PENDING':
      case 'UNDER_REVIEW':
        return <Clock className="h-4 w-4" />
      case 'REJECTED':
      case 'EXPIRED':
        return <XCircle className="h-4 w-4" />
      default:
        return <AlertCircle className="h-4 w-4" />
    }
  }

  const getDocumentTypeColor = (type) => {
    switch (type?.toUpperCase()) {
      case 'POLICY_DOCUMENT':
        return 'bg-blue-100 text-blue-800'
      case 'CLAIM_DOCUMENT':
        return 'bg-green-100 text-green-800'
      case 'KYC_DOCUMENT':
        return 'bg-purple-100 text-purple-800'
      case 'MEDICAL_REPORT':
        return 'bg-red-100 text-red-800'
      case 'INVOICE':
      case 'RECEIPT':
        return 'bg-orange-100 text-orange-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const getFileIcon = (extension) => {
    const ext = extension?.toLowerCase()
    if (['.pdf'].includes(ext)) return <File className="h-8 w-8 text-red-500" />
    if (['.jpg', '.jpeg', '.png', '.gif'].includes(ext)) return <ImageIcon className="h-8 w-8 text-blue-500" />
    return <FileText className="h-8 w-8 text-gray-500" />
  }

  const handleDownloadDocument = (document) => {
    if (document.file_url) {
      // If file URL exists, open in new tab
      window.open(document.file_url, '_blank')
      toast.success(`Opening ${document.file_name}`)
    } else {
      toast.error(`Download link not available for ${document.file_name}`)
    }
  }

  const handleViewDocument = (document) => {
    setSelectedDocument(document)
    setShowViewModal(true)
  }

  const closeViewModal = () => {
    setSelectedDocument(null)
    setShowViewModal(false)
  }

  const openUploadModal = async () => {
    setShowUploadModal(true)
    
    // Load user's policies and claims for the dropdowns
    try {
      const [policies, claimsData] = await Promise.all([
        getUserPolicies(userId),
        getUserPoliciesWithClaims(userId)
      ])
      
      setUserPolicies(policies || [])
      
      // Extract unique claims
      const uniqueClaims = claimsData?.filter(item => item.claim_id) || []
      setUserClaims(uniqueClaims)
    } catch (err) {
      console.error('Error loading policies/claims for upload:', err)
    }
  }

  const closeUploadModal = () => {
    setShowUploadModal(false)
    setUploadFile(null)
    setUploadProgress(0)
    setUploadDocType('CLAIM_DOCUMENT')
    setUploadCategory('MEDICAL')
    setUploadDescription('')
    setUploadSubcategory('')
    setUploadTags('')
    setUploadRelatedClaim('')
    setUploadRelatedPolicy('')
  }

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0]
    if (file) {
      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        toast.error('File size must be less than 10MB')
        return
      }
      
      // Validate file type
      const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png', 'image/gif']
      if (!allowedTypes.includes(file.type)) {
        toast.error('Only PDF and image files (JPG, PNG, GIF) are allowed')
        return
      }
      
      setUploadFile(file)
    }
  }

  const handleUpload = async () => {
    if (!uploadFile) {
      toast.error('Please select a file to upload')
      return
    }

    try {
      setUploading(true)
      setUploadProgress(10)

      // Generate unique file name
      const timestamp = Date.now()
      const fileExt = uploadFile.name.split('.').pop()
      const fileName = `${userId}_${timestamp}.${fileExt}`
      const filePath = `${userId}/${fileName}` // Path inside the bucket (don't include bucket name)

      setUploadProgress(30)

      let publicUrl = null

      // Try to upload file to Supabase Storage
      try {
        const { data: uploadData, error: uploadError } = await supabase.storage
          .from('documents')
          .upload(filePath, uploadFile, {
            cacheControl: '3600',
            upsert: false
          })

        if (uploadError) {
          console.warn('Storage bucket not found, creating database record only:', uploadError)
          // Continue without file upload - just create database record
        } else {
          // Get public URL if upload succeeded
          const { data: urlData } = supabase.storage
            .from('documents')
            .getPublicUrl(filePath)
          publicUrl = urlData?.publicUrl
        }
      } catch (storageError) {
        console.warn('Storage error, continuing with database record only:', storageError)
      }

      setUploadProgress(60)
      setUploadProgress(80)

      // Generate document number in format: DOC-YYYY-####
      const year = new Date().getFullYear()
      const randomNum = Math.floor(Math.random() * 10000).toString().padStart(4, '0')
      const documentNumber = `DOC-${year}-${randomNum}`

      // Create document record in database with user-specified metadata
      const tags = uploadTags ? uploadTags.split(',').map(t => t.trim()).filter(Boolean) : []
      
      const documentData = {
        document_number: documentNumber,
        document_name: uploadFile.name,
        document_description: uploadDescription || `Uploaded ${uploadDocType.replace(/_/g, ' ').toLowerCase()}`,
        document_type: uploadDocType,
        document_category: uploadCategory,
        document_subcategory: uploadSubcategory || null,
        file_name: uploadFile.name,
        file_extension: `.${fileExt}`,
        file_size_bytes: uploadFile.size,
        file_path: publicUrl ? filePath : 'pending_upload',
        file_url: publicUrl || null,
        mime_type: uploadFile.type,
        document_tags: tags.length > 0 ? tags : null,
        customer_id: userId,
        policy_id: uploadRelatedPolicy || null,
        claim_id: uploadRelatedClaim || null,
        uploaded_by: userId,
        uploaded_for: 'CUSTOMER',
        document_status: 'PENDING_VERIFICATION',
        verification_status: 'PENDING',
        processing_status: 'PENDING',
        processing_notes: publicUrl ? null : 'File uploaded, pending storage bucket configuration',
        created_by: userId,
        updated_by: userId
      }

      const newDoc = await createTableRecord('documents', documentData)

      setUploadProgress(100)
      
      toast.success('Document uploaded successfully!')
      
      // Refresh documents list
      await loadDocuments()
      
      // Close modal
      closeUploadModal()
      
    } catch (error) {
      console.error('Error uploading document:', error)
      toast.error('Failed to upload document')
    } finally {
      setUploading(false)
      setUploadProgress(0)
    }
  }

  const handleDeleteDocument = async (document) => {
    // Don't allow deleting policy documents
    if (document.document_type?.toUpperCase() === 'POLICY_DOCUMENT') {
      toast.error('Policy documents cannot be deleted')
      return
    }

    if (!window.confirm(`Are you sure you want to delete ${document.file_name}? This action cannot be undone.`)) {
      return
    }

    try {
      // Delete from storage if file exists
      if (document.file_path && document.file_path !== 'pending_upload') {
        try {
          await supabase.storage
            .from('documents')
            .remove([document.file_path])
        } catch (storageError) {
          console.warn('Could not delete from storage:', storageError)
          // Continue with database deletion even if storage delete fails
        }
      }

      // Delete from database
      await deleteTableRecord('documents', document.document_id, 'document_id')
      
      toast.success('Document deleted successfully')
      
      // Refresh documents list
      await loadDocuments()
      
    } catch (error) {
      console.error('Error deleting document:', error)
      toast.error('Failed to delete document')
    }
  }

  // Filter documents
  const filteredDocuments = documents.filter(doc => {
    // Type filter
    if (typeFilter !== 'all' && doc.document_type?.toUpperCase() !== typeFilter.toUpperCase()) {
      return false
    }

    // Status filter
    if (statusFilter !== 'all' && doc.document_status?.toUpperCase() !== statusFilter.toUpperCase()) {
      return false
    }

    // Search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase()
      return (
        doc.document_number?.toLowerCase().includes(searchLower) ||
        doc.document_name?.toLowerCase().includes(searchLower) ||
        doc.file_name?.toLowerCase().includes(searchLower)
      )
    }

    return true
  })

  // Calculate statistics
  const verifiedDocs = documents.filter(d => d.document_status?.toUpperCase() === 'VERIFIED').length
  const pendingDocs = documents.filter(d => d.document_status?.toUpperCase().includes('PENDING')).length
  const claimDocs = documents.filter(d => d.document_type?.toUpperCase() === 'CLAIM_DOCUMENT').length

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading documents...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-red-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading Documents</h3>
          <p className="text-red-500">{error}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-semibold text-gray-900">My Documents</h1>
          <p className="text-xs sm:text-sm text-gray-600">View all your policy and claim documents</p>
        </div>
        <button
          onClick={openUploadModal}
          className="flex items-center gap-2 px-3 sm:px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm sm:text-base whitespace-nowrap w-full sm:w-auto justify-center"
        >
          <Upload className="h-4 w-4" />
          <span className="hidden sm:inline">Upload Document</span>
          <span className="sm:hidden">Upload</span>
        </button>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200 rounded-xl p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-blue-800">Total Documents</p>
              <p className="text-2xl font-bold text-blue-900 mt-1">{documents.length}</p>
            </div>
            <div className="bg-blue-200 p-3 rounded-full">
              <FileText className="h-6 w-6 text-blue-700" />
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-green-50 to-green-100 border border-green-200 rounded-xl p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-green-800">Verified</p>
              <p className="text-2xl font-bold text-green-900 mt-1">{verifiedDocs}</p>
            </div>
            <div className="bg-green-200 p-3 rounded-full">
              <FileCheck className="h-6 w-6 text-green-700" />
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 border border-yellow-200 rounded-xl p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-yellow-800">Pending Review</p>
              <p className="text-2xl font-bold text-yellow-900 mt-1">{pendingDocs}</p>
            </div>
            <div className="bg-yellow-200 p-3 rounded-full">
              <Clock className="h-6 w-6 text-yellow-700" />
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border border-gray-200 rounded-xl p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search documents..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Type Filter */}
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Types</option>
              <option value="policy_document">Policy Documents</option>
              <option value="claim_document">Claim Documents</option>
              <option value="kyc_document">KYC Documents</option>
              <option value="medical_report">Medical Reports</option>
            </select>
          </div>

          {/* Status Filter */}
          <div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Status</option>
              <option value="verified">Verified</option>
              <option value="pending_verification">Pending</option>
              <option value="under_review">Under Review</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
        </div>
      </div>

      {/* Documents List */}
      {filteredDocuments.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-xl p-12 text-center">
          <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Documents Found</h3>
          <p className="text-gray-500">
            {searchTerm || typeFilter !== 'all' || statusFilter !== 'all'
              ? 'Try adjusting your filters'
              : 'No documents uploaded yet'}
          </p>
          <button
            onClick={openUploadModal}
            className="mt-4 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Upload Your First Document
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {filteredDocuments.map((document) => (
            <div
              key={document.document_id}
              className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-all hover:scale-105"
            >
              {/* Document Icon & Name */}
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center space-x-2">
                  {getFileIcon(document.file_extension)}
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-gray-900 text-xs line-clamp-2 leading-tight">
                      {document.document_name || document.file_name || 'Untitled'}
                    </h3>
                  </div>
                </div>
              </div>

              {/* Document Type & Status */}
              <div className="flex flex-col space-y-1 mb-3">
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium self-start ${getDocumentTypeColor(document.document_type)}`}>
                  {document.document_type?.replace(/_/g, ' ').substring(0, 15) || 'Document'}
                </span>
                <span className={`inline-flex items-center space-x-1 px-2 py-0.5 rounded-full text-xs font-medium border self-start ${getStatusColor(document.document_status)}`}>
                  {getStatusIcon(document.document_status)}
                  <span>{document.document_status?.replace(/_/g, ' ').substring(0, 10) || 'Unknown'}</span>
                </span>
              </div>

              {/* Compact Details */}
              <div className="space-y-1 text-xs text-gray-600 mb-3">
                <div className="flex items-center justify-between">
                  <span>Size:</span>
                  <span className="font-medium">{formatFileSize(document.file_size_bytes)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span>Type:</span>
                  <span className="font-medium">{document.file_extension?.toUpperCase() || 'N/A'}</span>
                </div>
              </div>

              {/* Compact Actions */}
              <div className="grid grid-cols-3 gap-1">
                <button
                  onClick={() => handleViewDocument(document)}
                  className="flex items-center justify-center space-x-1 px-2 py-1.5 bg-blue-50 text-blue-600 rounded hover:bg-blue-100 transition-colors text-xs"
                >
                  <Eye className="h-3 w-3" />
                </button>
                <button
                  onClick={() => handleDownloadDocument(document)}
                  className="flex items-center justify-center space-x-1 px-2 py-1.5 bg-green-50 text-green-600 rounded hover:bg-green-100 transition-colors text-xs"
                >
                  <Download className="h-3 w-3" />
                </button>
                {/* Only show delete button for non-policy documents */}
                {document.document_type?.toUpperCase() !== 'POLICY_DOCUMENT' && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      handleDeleteDocument(document)
                    }}
                    className="flex items-center justify-center space-x-1 px-2 py-1.5 bg-red-50 text-red-600 rounded hover:bg-red-100 transition-colors text-xs"
                  >
                    <Trash2 className="h-3 w-3" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Document Categories Summary */}
      {filteredDocuments.length > 0 && (
        <div className="bg-purple-50 border border-purple-200 rounded-xl p-6">
          <h3 className="text-sm font-semibold text-purple-900 mb-4">Document Summary</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <p className="text-purple-700">Policy Docs:</p>
              <p className="font-semibold text-purple-900 text-lg">
                {filteredDocuments.filter(d => d.document_type?.toUpperCase() === 'POLICY_DOCUMENT').length}
              </p>
            </div>
            <div>
              <p className="text-purple-700">Claim Docs:</p>
              <p className="font-semibold text-purple-900 text-lg">
                {filteredDocuments.filter(d => d.document_type?.toUpperCase() === 'CLAIM_DOCUMENT').length}
              </p>
            </div>
            <div>
              <p className="text-purple-700">Medical Reports:</p>
              <p className="font-semibold text-purple-900 text-lg">
                {filteredDocuments.filter(d => d.document_type?.toUpperCase() === 'MEDICAL_REPORT').length}
              </p>
            </div>
            <div>
              <p className="text-purple-700">KYC Docs:</p>
              <p className="font-semibold text-purple-900 text-lg">
                {filteredDocuments.filter(d => d.document_type?.toUpperCase() === 'KYC_DOCUMENT').length}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Help Text */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
        <h3 className="text-sm font-semibold text-blue-900 mb-2">About Documents</h3>
        <p className="text-sm text-blue-800">
          This page shows all documents related to your policies and claims. You can upload new documents, 
          view existing ones, and track their verification status.
        </p>
      </div>

      {/* Upload Document Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[85vh] overflow-y-auto shadow-2xl">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 sticky top-0 bg-white z-10">
              <h2 className="text-lg font-bold text-gray-900">Upload Document</h2>
              <button
                onClick={closeUploadModal}
                disabled={uploading}
                className="text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              {/* Compact Grid: Type, Category, Subcategory */}
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">
                    Type *
                  </label>
                  <select
                    value={uploadDocType}
                    onChange={(e) => setUploadDocType(e.target.value)}
                    disabled={uploading}
                    className="w-full px-2 py-1.5 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 text-xs"
                  >
                    <option value="CLAIM_DOCUMENT">CLAIM_DOCUMENT</option>
                    <option value="MEDICAL_REPORT">MEDICAL_REPORT</option>
                    <option value="KYC_DOCUMENT">KYC_DOCUMENT</option>
                    <option value="INVOICE">INVOICE</option>
                    <option value="RECEIPT">RECEIPT</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">
                    Category *
                  </label>
                  <select
                    value={uploadCategory}
                    onChange={(e) => setUploadCategory(e.target.value)}
                    disabled={uploading}
                    className="w-full px-2 py-1.5 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 text-xs"
                  >
                    <option value="MEDICAL">MEDICAL</option>
                    <option value="FINANCIAL">FINANCIAL</option>
                    <option value="IDENTITY">IDENTITY</option>
                    <option value="ADDRESS">ADDRESS</option>
                    <option value="LEGAL">LEGAL</option>
                    <option value="OTHER">OTHER</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">
                    Subcategory
                  </label>
                  <input
                    type="text"
                    value={uploadSubcategory}
                    onChange={(e) => setUploadSubcategory(e.target.value.toUpperCase())}
                    disabled={uploading}
                    placeholder="e.g., HOSPITAL_BILL"
                    className="w-full px-2 py-1.5 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 text-xs uppercase"
                  />
                </div>
              </div>

              {/* Description & Tags */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">
                    Description
                  </label>
                  <textarea
                    value={uploadDescription}
                    onChange={(e) => setUploadDescription(e.target.value)}
                    disabled={uploading}
                    rows={2}
                    placeholder="Brief description..."
                    className="w-full px-2 py-1.5 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 resize-none text-xs"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">
                    Tags (comma-separated)
                  </label>
                  <textarea
                    value={uploadTags}
                    onChange={(e) => setUploadTags(e.target.value)}
                    disabled={uploading}
                    rows={2}
                    placeholder="claim, surgery, hospital"
                    className="w-full px-2 py-1.5 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 resize-none text-xs"
                  />
                </div>
              </div>

              {/* Related Claim and Policy */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">
                    Related Claim
                  </label>
                  <select
                    value={uploadRelatedClaim}
                    onChange={(e) => {
                      const claimId = e.target.value
                      setUploadRelatedClaim(claimId)
                      
                      // Auto-select linked policy when claim is selected
                      if (claimId) {
                        const selectedClaim = userClaims.find(c => c.claim_id === claimId)
                        if (selectedClaim && selectedClaim.policy_id) {
                          setUploadRelatedPolicy(selectedClaim.policy_id)
                        }
                      }
                    }}
                    disabled={uploading}
                    className="w-full px-2 py-1.5 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 text-xs"
                  >
                    <option value="">-- None --</option>
                    {userClaims.map(claim => (
                      <option key={claim.claim_id} value={claim.claim_id}>
                        {claim.claim_number}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">
                    Related Policy
                  </label>
                  <select
                    value={uploadRelatedPolicy}
                    onChange={(e) => setUploadRelatedPolicy(e.target.value)}
                    disabled={uploading}
                    className="w-full px-2 py-1.5 border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 text-xs"
                  >
                    <option value="">-- None --</option>
                    {userPolicies.map(policy => (
                      <option key={policy.policy_id} value={policy.policy_id}>
                        {policy.policy_number}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* File Upload Area */}
              <div className="border-t border-gray-200 pt-4">
                <label className="block text-xs font-medium text-gray-700 mb-1.5">
                  Select File *
                </label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-blue-400 transition-colors bg-gray-50">
                  <input
                    type="file"
                    onChange={handleFileSelect}
                    accept=".pdf,.jpg,.jpeg,.png,.gif"
                    className="hidden"
                    id="file-upload"
                    disabled={uploading}
                  />
                  <label
                    htmlFor="file-upload"
                    className="cursor-pointer flex flex-col items-center"
                  >
                    <Upload className="h-8 w-8 text-gray-400 mb-1.5" />
                    <p className="text-xs text-gray-600 font-medium">
                      Click to upload
                    </p>
                    <p className="text-xs text-gray-500">
                      PDF, JPG, PNG, GIF (max 10MB)
                    </p>
                  </label>
                </div>
                
                {/* Selected File Info */}
                {uploadFile && (
                  <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <FileText className="h-3.5 w-3.5 text-blue-600" />
                        <div>
                          <p className="text-xs font-medium text-gray-900">{uploadFile.name}</p>
                          <p className="text-xs text-gray-500">{formatFileSize(uploadFile.size)}</p>
                        </div>
                      </div>
                      {!uploading && (
                        <button
                          onClick={() => setUploadFile(null)}
                          className="text-gray-400 hover:text-gray-600"
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Upload Progress */}
              {uploading && (
                <div className="bg-blue-50 border border-blue-200 rounded p-3">
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-xs font-medium text-gray-700">Uploading...</span>
                    <span className="text-xs font-medium text-blue-600">{uploadProgress}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-1.5">
                    <div
                      className="bg-blue-600 h-1.5 rounded-full transition-all duration-300"
                      style={{ width: `${uploadProgress}%` }}
                    ></div>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex space-x-2 pt-4 border-t border-gray-200 sticky bottom-0 bg-white">
                <button
                  onClick={closeUploadModal}
                  disabled={uploading}
                  className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 text-sm font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpload}
                  disabled={!uploadFile || uploading}
                  className="flex-1 px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium shadow-sm"
                >
                  {uploading ? 'Uploading...' : 'Upload Document'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* View Document Modal */}
      {showViewModal && selectedDocument && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">Document Details</h2>
              <button
                onClick={closeViewModal}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              {/* Document Info */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">{selectedDocument.document_name}</h3>
                <p className="text-sm text-gray-600">{selectedDocument.document_number}</p>
              </div>

              {/* Status Badges */}
              <div className="flex items-center space-x-2">
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${getDocumentTypeColor(selectedDocument.document_type)}`}>
                  {selectedDocument.document_type?.replace(/_/g, ' ')}
                </span>
                <span className={`inline-flex items-center space-x-1 px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(selectedDocument.document_status)}`}>
                  {getStatusIcon(selectedDocument.document_status)}
                  <span>{selectedDocument.document_status?.replace(/_/g, ' ')}</span>
                </span>
              </div>

              {/* Document Details Grid */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-500">File Name</p>
                  <p className="font-medium text-gray-900">{selectedDocument.file_name}</p>
                </div>
                <div>
                  <p className="text-gray-500">File Size</p>
                  <p className="font-medium text-gray-900">{formatFileSize(selectedDocument.file_size_bytes)}</p>
                </div>
                <div>
                  <p className="text-gray-500">Upload Date</p>
                  <p className="font-medium text-gray-900">{formatDate(selectedDocument.created_at)}</p>
                </div>
                <div>
                  <p className="text-gray-500">Category</p>
                  <p className="font-medium text-gray-900">{selectedDocument.document_category?.replace(/_/g, ' ') || 'N/A'}</p>
                </div>
              </div>

              {/* Description */}
              {selectedDocument.document_description && (
                <div>
                  <p className="text-gray-500 text-sm mb-1">Description</p>
                  <p className="text-gray-900">{selectedDocument.document_description}</p>
                </div>
              )}

              {/* Verification Info */}
              {selectedDocument.verification_notes && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <p className="text-sm font-semibold text-green-900 mb-1">Verification Notes</p>
                  <p className="text-sm text-green-800">{selectedDocument.verification_notes}</p>
                  {selectedDocument.verified_by && (
                    <p className="text-xs text-green-700 mt-2">
                      Verified by: {selectedDocument.verified_by} on {formatDate(selectedDocument.verified_at)}
                    </p>
                  )}
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex space-x-3 pt-4 border-t border-gray-200">
                {selectedDocument.file_url && (
                  <button
                    onClick={() => {
                      window.open(selectedDocument.file_url, '_blank')
                      toast.success('Opening document in new tab')
                    }}
                    className="flex-1 flex items-center justify-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <Eye className="h-4 w-4" />
                    <span>Open</span>
                  </button>
                )}
                <button
                  onClick={() => handleDownloadDocument(selectedDocument)}
                  className="flex-1 flex items-center justify-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                  <Download className="h-4 w-4" />
                  <span>Download</span>
                </button>
                {/* Show delete button only for non-policy documents */}
                {selectedDocument.document_type?.toUpperCase() !== 'POLICY_DOCUMENT' && (
                  <button
                    onClick={() => {
                      closeViewModal()
                      handleDeleteDocument(selectedDocument)
                    }}
                    className="flex-1 flex items-center justify-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                  >
                    <Trash2 className="h-4 w-4" />
                    <span>Delete</span>
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default CustomerDocuments

